import type { OpeningHour, Salon } from "@/data/types";

const DAY_ORDER: OpeningHour["day"][] = ["Sön", "Mån", "Tis", "Ons", "Tor", "Fre", "Lör"];

function nowInStockholm(): { day: OpeningHour["day"]; minutes: number } {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Stockholm",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  const map: Record<string, OpeningHour["day"]> = {
    Sun: "Sön", Mon: "Mån", Tue: "Tis", Wed: "Ons", Thu: "Tor", Fri: "Fre", Sat: "Lör",
  };
  return { day: map[weekday] ?? "Mån", minutes: hour * 60 + minute };
}

function parseRange(hours: string): { open: number; close: number } | null {
  const m = hours.match(/(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return {
    open: Number(m[1]) * 60 + Number(m[2]),
    close: Number(m[3]) * 60 + Number(m[4]),
  };
}

export function getOpenStatus(salon: Pick<Salon, "openingHours">): {
  open: boolean;
  todayHours: string | null;
  nextOpen?: { day: OpeningHour["day"]; hours: string };
} {
  const { day, minutes } = nowInStockholm();
  const today = salon.openingHours.find((h) => h.day === day);
  const range = today ? parseRange(today.hours) : null;
  const isOpen = !!range && minutes >= range.open && minutes < range.close;
  if (isOpen) return { open: true, todayHours: today!.hours };

  // find next open day (including later today if before opening)
  const startIdx = DAY_ORDER.indexOf(day);
  for (let i = 0; i < 7; i++) {
    const d = DAY_ORDER[(startIdx + i) % 7];
    const entry = salon.openingHours.find((h) => h.day === d);
    if (!entry) continue;
    const r = parseRange(entry.hours);
    if (!r) continue;
    if (i === 0 && minutes < r.open) {
      return { open: false, todayHours: today?.hours ?? null, nextOpen: { day: d, hours: entry.hours } };
    }
    if (i > 0) {
      return { open: false, todayHours: today?.hours ?? null, nextOpen: { day: d, hours: entry.hours } };
    }
  }
  return { open: false, todayHours: today?.hours ?? null };
}
